/// <amd-module name="SearchUI/Components/PathSelector/PathSelector" />

import * as React from "react";
import { Button } from "VSSUI/Button";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { getId } from 'VSS/Core/Util/Object';
import { css, KeyCode } from 'VSSUI/Util';
import { Icon, IconSize } from 'VSSUI/Icon';
import { PathSelectorMenu } from "SearchUI/Components/PathSelector/PathSelectorMenu";
import { IPathSelectorProps, PathApplicationMode, ContentLoadState, Mode } from "SearchUI/Components/PathSelector/PathSelector.Props";
import { CalloutComponent } from "SearchUI/Components/Callout";
import * as Resources from "SearchUI/Search.Resources.SearchUI";

import "VSS/LoaderPlugins/Css!SearchUI/Components/PathSelector/PathSelector";

const roots = ["/", "\\", "$/"];

export interface IPathSelectorState {
    items: any[];

    active: boolean;

    searchText: string;

    mode: Mode;

    activeDescendantIndex: number;

    calloutShown: boolean;
}

export class PathSelector extends React.Component<IPathSelectorProps, IPathSelectorState> {
    private _viewState: IPathSelectorState;
    private _textBox: HTMLElement;
    private _pathSelector: HTMLElement;
    private _helpInfoRef: React.RefObject<HTMLElement>;

    constructor(props: IPathSelectorProps) {
        super(props);
        this.state = this._viewState = this._getInitialState(props);
        this._helpInfoRef = React.createRef<HTMLElement>();
    }

    public render(): JSX.Element {
        const menuButtonId = getId("search-Filters-DropdownButtonLabel-"),
            pathSelectorMenuId = getId("search-Filter-Path-Selector-Menu-"),
            menuComboBoxAttributes = this._getMenuComboBoxAttributes(),
            inputBoxAttributes = this._getInputBoxAttributes(pathSelectorMenuId),
            showRemoveTextIcon: boolean = this.state.searchText && this.state.active,
            showComboBoxLabel: boolean = !this.props.enabled || !this.state.active,
            footerMessageElement = this.props.onGetFooterMessage(this.state.items.length, this.state.searchText, this.state.mode),
            disabledInfoId = getId("search-DisabledFilter-Info-"),
            calloutId = getId("search-Filter--CalloutId");

        return (
            <div>
                <div className={css("search-PathSelector--container")}
                    onBlur={this._onMenuBlur}
                    ref={(element) => { this._pathSelector = element }} >
                    <div
                        className={css(
                            "filter-ComboBox--container", !this.props.enabled && "is-disabled")}
                        aria-describedby={calloutId}
                        aria-expanded={this.state.active}
                        {...menuComboBoxAttributes}>
                        {
                            // render the label and the input box
                            <div className={css("filter-ComboBox")}>
                                {
                                    showComboBoxLabel &&
                                    <span className={css(
                                        "comboBox-displayLabel", !this.props.enabled && "is-disabled")}>
                                        {this.props.label + ": "}
                                    </span>
                                }
                                <input
                                    className={css("comboBox-input", !this.props.enabled && "is-disabled")}
                                    {...inputBoxAttributes} />
                            </div>
                        }
                        {
                            this.props.enabled &&
                            showRemoveTextIcon &&
                            <Button
                                className="clear-icon"
                                iconProps={{ iconName: "Clear", size: IconSize.small }}
                                subtle={true}
                                onClick={this._onRemoveClick}
                                ariaLabel={this.props.searchBoxClearTextAriaLabel}
                            />
                        }
                        {
                            // render "information" icon
                            !this.props.enabled &&
                            <span
                                ref={this._helpInfoRef}
                                id={disabledInfoId}
                                className={css("disabled-Info-icon", "bowtie-status-help-outline", "is-disabled")} />
                        }
                        {
                            showRemoveTextIcon
                                ? <Button
                                    className={css("filter-GoButton", !this.props.enabled && "is-disabled")}
                                    iconProps={{ iconName: "Forward", size: IconSize.small }}
                                    subtle={true}
                                    onClick={this._applyPath}
                                    ariaLabel={this.props.applyButtonAriaLabel}
                                />
                                : <Button
                                    className={css("filter-Chevron", !this.props.enabled && "is-disabled")}
                                    iconProps={{ iconName: "ChevronDown", size: IconSize.small }}
                                    subtle={true}
                                    disabled={!this.props.enabled}
                                    onClick={this._openPathDropdown}
                                    ariaLabel={this.props.label + " " + Resources.ComboBoxDropButtonText}
                                />
                        }
                    </div>
                    {
                        // Render the path dropdown
                        this.props.enabled &&
                        this.state.active &&
                        this.props.contentLoadState !== ContentLoadState.Loading &&
                        <PathSelectorMenu
                            id={pathSelectorMenuId}
                            items={this.state.items}
                            currentItemIndex={this.state.activeDescendantIndex}
                            onItemClick={this._onItemClick}
                            footerMessageElement={footerMessageElement}
                            onItemRender={(item: any) => {
                                // Bold the hit content if the mode is "Searching" or "None".
                                // We don't want higlights if user is navigating or navigating after searching.
                                let needBolding = (this._viewState.mode === Mode.Searching || this._viewState.mode === Mode.None);
                                return this.props.behaviour.onItemRender(item, this.state.searchText, needBolding)
                            }} />
                    }
                    {
                        this.state.active &&
                        this.props.contentLoadState === ContentLoadState.Loading &&
                        <div className={css("content-Loading", "ease-out")}>
                            <Spinner label="Loading..." size={SpinnerSize.large} />
                        </div>
                    }
                </div>
                {
                    // Render call out component
                    this.state.calloutShown &&
                    this.props.calloutProps &&
                    <CalloutComponent
                        targetElement={this._helpInfoRef.current}
                        title={this.props.calloutProps["title"]}
                        content={this.props.calloutProps["content"]}
                        id={calloutId} />
                }
            </div>);
    }

    public componentWillReceiveProps(newProps: IPathSelectorProps): void {
        // doing a bare-minimum comparisons for perf reasons.
        const itemsUpdated: boolean = newProps.items.length !== this.props.items.length;
        const defaultItemUpdated: boolean = newProps.defaultSelectedItem !== this.props.defaultSelectedItem;

        if (itemsUpdated || defaultItemUpdated) {
            // Set the callout state so that is hidden when the control gets enabled after getting the search response.
            this.setState(this._getInitialState(newProps));
        }
    }

    /**
     * This function provides the attributes of the combo-box based on its state (disabled/enabled)
     * When the control is disabled, the container is tabbable and has a role of a "combobox" with disabled attribute in place.
     * When the control is enabled, the inner text box is modified to behave like a "combobox" so there is no need to set role for
     * the container explicitly.
     */
    private _getMenuComboBoxAttributes(): any {
        let attributes = {
            "aria-label": this.props.label
        };

        if (this.props.enabled) {
            attributes["onClick"] = this._onPathMenuComboBoxClick;
        }
        else {
            attributes["onFocus"] = () => this._triggerCallout(true);
            attributes["onBlur"] = () => this._triggerCallout(false);
            attributes["role"] = "combobox";
            attributes["tabIndex"] = (!this.props.enabled ? -1 : 0);
            attributes["disabled"] = true;
            attributes["aria-disabled"] = true;
            attributes["onMouseOver"] = () => this._triggerCallout(true);
            attributes["onMouseLeave"] = () => this._triggerCallout(false);
            attributes["onKeyDown"] = this._onKeyDown;
        }

        return attributes;
    }

    /**
     * This function provides the attributes of the input element of the path control based on its state
     * When the control is enabled, the input box should behave like a combobox and is the first tabbable element
     */
    private _getInputBoxAttributes(ariaOwnsId: string): any {
        let attributes = {
            "ref": (text) => { this._textBox = text },
            "aria-label": this.props.label + " " + this.state.searchText,
            "spellCheck": false
        },
            label = this.state.activeDescendantIndex < 0 ||
                !(this.state.mode & Mode.Navigating)
                ? this.state.searchText
                : this.props.behaviour.getPath(this.state.items[this.state.activeDescendantIndex]);

        if (this.props.enabled) {
            attributes["aria-expanded"] = this.state.active;
            attributes["role"] = "combobox";
            attributes["aria-autocomplete"] = "list";
            attributes["value"] = label;
            attributes["onKeyDown"] = this._onTextBoxKeyDown;
            attributes["onChange"] = this._onSearchTextChange;
            attributes["onFocus"] = this._onSearchTextBoxFocus;
            attributes["onClick"] = this._activateSearchBox;
            attributes["placeholder"] = this.props.searchTextPlaceholder;
            attributes["aria-owns"] = ariaOwnsId;
        }
        else {
            attributes["disabled"] = true;
            attributes["value"] = "";
        }

        return attributes;
    }

    private _openPathDropdown = (evt): void => {
        if (evt.type === 'click' || evt.keyCode === KeyCode.enter) {
            if (this.state.active) {
                this._closeDropdown(true);
            }
            else {
                this._textBox.focus();
            }

            if (this.props.onClick) {
                this.props.onClick();
            }

            //Prevent the path menu to react on Mouse Click. 
            evt.stopPropagation();
        }
    }

    /**
     * Clicking the menu button should activate the text box for search purpose and bring down the menu
     */
    private _onPathMenuComboBoxClick = (): void => {
        if (this.props.onClick && !this.state.active) {
            this.props.onClick();
        }
        this._textBox.focus();
    }

    /**
     * On Clicking the menu item update the selected item, text in menu box and trigger item selection action.
     * @param item
     */
    private _onItemClick = (item: any): void => {
        this._applyItem(item, true);
    }

    /**
     * Applied the passed item selection for the menu.
     * @param item
     */
    private _applyItem = (item: {} | string, validated: boolean): void => {
        const { items, behaviour } = this.props;
        const { pathApplicationMode, getPath } = behaviour;
        const path = typeof item !== "string" ? getPath(item).toLowerCase() : item.toLowerCase();
        const shouldApplyPath: boolean = validated ||
            pathApplicationMode === PathApplicationMode.allowAll ||
            items.some(i => path === getPath(i).toLowerCase());

        if (this.props.onSelectionChanged && shouldApplyPath) {
            this.props.onSelectionChanged(this.props.name, path);
        }

        this._closeDropdown(false, item);
    }

    /**
     * Call back funtion when there is a click on the arrow button.
     * Applies the item name in the text box if the menu is enabled.
     */
    private _applyPath = (evt): void => {
        if (evt.type === 'click' || evt.keyCode === KeyCode.enter) {
            const activeItemIndex = this.state.activeDescendantIndex;
            const { items } = this.state;

            // If there is an active item after navigating the list of items apply it,
            // otherwise apply the text in the textbox if it is a valid path.
            if (activeItemIndex >= 0 && activeItemIndex < items.length) {
                this._applyItem(items[activeItemIndex], true);
            }
            else {
                this._applyItem(this.state.searchText, false)
            }

            //Prevent the path menu to react on Mouse Click. 
            evt.stopPropagation();
        }
    }

    /**
     * Handle Keyboard navigation when focus is on disabled filter bar
     * @param evt
     */
    private _onKeyDown = (evt): void => {
        if (evt.keyCode === KeyCode.escape) {
            this._triggerCallout(false);
        }
    }

    /**
     * Trigger callout when hover happens on the search box or when it is under focus.
     * @param showCallout
     */
    private _triggerCallout = (showCallout: boolean): void => {
        if (this.props.triggerCallout) {
            this.props.triggerCallout(showCallout);
        }

        this.setState({ calloutShown: showCallout });
    }

    /**
     * On change in text in the text box, update the list visible to the user based on the text.
     * If the user enters /tfs/ the last character "/" will be truncated and results will be shown
     * @param evt
     */
    private _onSearchTextChange = (evt): void => {
        const searchText = evt.target.value,
            filterText: string = this._removeLastSlash(searchText.replace(/\/+/g, "/")),
            allPaths: any[] = this.props.items,
            { getPath } = this.props.behaviour;

        if (filterText.length > 0) {
            let results = substringSort(allPaths, getPath, filterText.toLowerCase());

            this._viewState.items = results;
            // searchText might get modified in the above trimming step so using evt.target.value
            this._viewState.searchText = searchText;
        }
        else {
            this._viewState.items = allPaths;
            this._viewState.searchText = "";
        }

        this._viewState.mode = Mode.Searching;
        this._viewState.activeDescendantIndex = 0;

        // Setting the state to active for opening the dropdown for cases when the user has closed the dropdown by ESC
        this._viewState.active = true;
        this.setState(this._viewState);
    }

    private _removeLastSlash(text: string): string {
        const lastCharacter = text.slice(-1);

        if (text.length > 1 &&
            (lastCharacter === "/" ||
                lastCharacter === "\\")) {
            text = text.slice(0, -1);
        }

        return text;
    }

    /**
     * Handle Keyboard navigation when focus is on Text Box
     * @param evt
     */
    private _onTextBoxKeyDown = (evt): void => {
        const { items } = this.state,
            itemCount: number = items.length;

        if (evt.keyCode === KeyCode.upArrow || evt.keyCode === KeyCode.downArrow) {
            if (itemCount === 0) {
                return;
            }

            let currentIndex = this.state.activeDescendantIndex;

            // Decrease the index in case of UP and increase for DOWN
            if (evt.keyCode === KeyCode.upArrow) {
                currentIndex >= 0 ? --currentIndex : currentIndex = itemCount - 1;
            }
            else {
                currentIndex < itemCount - 1 ? ++currentIndex : currentIndex = -1;
            }

            this._viewState.activeDescendantIndex = currentIndex;
            this._viewState.mode |= Mode.Navigating;

            this.setState(this._viewState);
            evt.preventDefault();
        }
        else if (evt.keyCode === KeyCode.escape) {
            this._closeDropdown(true);
        }
        else if (evt.keyCode === KeyCode.enter) {
            let { activeDescendantIndex, items } = this.state;

            if (activeDescendantIndex >= 0 && activeDescendantIndex < itemCount) {
                // If there is an active element in the dropdown apply that item.
                this._applyItem(items[activeDescendantIndex], true);
            }
            else {
                // Else try applying whatever user has typed in.
                this._applyItem(this.state.searchText, false);
            }
        }
    }

    /**
     * On Clicking the cross button next to text box erase the text in the text box.
     * @param evt
     */
    private _onRemoveClick = (evt): void => {
        if (evt.type === 'click' || evt.keyCode === KeyCode.enter) {
            this._textBox.focus();
            this._viewState.searchText = "";
            this._viewState.activeDescendantIndex = -1;
            this._viewState.mode = Mode.Searching;
            this._viewState.items = this.props.items;
            this.setState(this._viewState);
        }
    }

    /**
     * Callback when the text box is in focus.
     * Highlights the text and and activates the text box
     */
    private _onSearchTextBoxFocus = (event): void => {
        const inputElement = event.target as HTMLInputElement;

        // Highlight the input text when focus comes to input element.
        inputElement.select();
        this._activateSearchBox(event);
    }

    /**
     * Activates the text boxm, show one level elements.
     */
    private _activateSearchBox = (evt): void => {
        const text = (evt.target as HTMLInputElement).value;

        if (!this.state.active) {
            const { items, behaviour } = this.props;
            this._viewState.items = behaviour.getItemsOnActivation(items, text);
            this._viewState.activeDescendantIndex = behaviour.getActivatedItemIndexOnActivation(items, text);
        }

        this._viewState.active = true;
        this._viewState.searchText = text;
        this.setState(this._viewState);

        if (this.props.onClick) {
            this.props.onClick();
        }
    }

    /**
     * Close the menu when there is Blur event triggered from outside the path control
     * @param evt
     */
    private _onMenuBlur = (evt): void => {
        const relatedTarget = evt.relatedTarget || document.activeElement;
        if (!this._pathSelector.contains(relatedTarget)) {
            this._closeDropdown(true);
        }
    }

    /**
     * Close the dropdown and display the selected item in the menu box.
     */
    private _closeDropdown(reset: boolean, item?: any): void {
        this._viewState.active = false;
        this._viewState.activeDescendantIndex = -1;
        this._viewState.mode = Mode.None;

        const { defaultSelectedItem, behaviour } = this.props;

        const defaultText = defaultSelectedItem ? behaviour.getPath(defaultSelectedItem) : "";

        // If an item is selected set the text of the selected item
        // Else, if reset option is set restore the default path.
        // Else let the search text as is, whatever the user has keyed in.
        this._viewState.searchText = (item && behaviour.getPath(item)) || (reset && defaultText) || this.state.searchText;
        this.setState(this._viewState);
    }

    /**
     * Provides state of the component on basis of props provided.
     * @param props
     */
    private _getInitialState(props: IPathSelectorProps): IPathSelectorState {
        const { getPath } = props.behaviour;

        return {
            active: false,
            items: props.items,
            searchText: props.defaultSelectedItem ? getPath(props.defaultSelectedItem) : "",
            mode: Mode.None,
            activeDescendantIndex: -1,
            calloutShown: false
        };
    }
}

/**
 * Compares two string for the occurrence of the searchText
 * Ranks string with exact match higher, then the one in which searchText substring occurs quite early
 * if there is a tie, ranks the two strings based on lexicographic order.
 * @param first
 * @param second
 * @param searchText
 */
function compare(first: string, second: string, searchText: string): number {
    let substringIndexFirst = first.indexOf(searchText),
        substringIndexSecond = second.indexOf(searchText);

    if (substringIndexFirst !== substringIndexSecond) {
        return substringIndexFirst - substringIndexSecond;
    }
    else if (substringIndexFirst === 0 && substringIndexSecond === 0) {
        return first.toLowerCase() === searchText.toLowerCase()
            ? -1
            : (second.toLowerCase() === searchText.toLowerCase()
                ? 1
                : (first.toLowerCase() < second.toLowerCase()
                    ? -1
                    : 1));
    }
    else {
        return 0;
    }
}

function substringSort<TItem>(items: TItem[], getItemText: (item: TItem) => string, text: string): TItem[] {
    let results = items
        .filter((item) => getItemText(item).toLowerCase().indexOf(text.toLowerCase()) >= 0)
        .sort((a, b) => compare(getItemText(a).toLowerCase(), getItemText(b).toLowerCase(), text.toLowerCase()));
    return results;
}
